import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Whether the user has already dismissed the attractions banner in this page session (FE2-T168) —
 * the web equivalent of Android's `AttractionsEntryState` object.
 *
 * Once dismissed, the banner stops auto-revealing on scroll and the floating puck takes over as the
 * way back in — until the page is reloaded, at which point the banner is armed again.
 *
 * **Why a root-provided singleton and not component state, `sessionStorage` or `localStorage`.**
 * - A field on `MenuComponent` cannot carry it: the router recreates the component on every return
 *   from `/attractions` or `/category/:id`, so a dismissal kept there would silently undo itself and
 *   the banner would pop back up behind the user. (This is the web's version of the same problem
 *   Android has when a tab switch destroys Home's back-stack entry.)
 * - `localStorage` would make the dismissal permanent across visits, and `sessionStorage` would keep
 *   it across a reload. Both are more than was asked for: the banner is meant to re-arm on reload.
 *
 * A root-provided singleton's lifetime *is* the page session, which is exactly the requested scope.
 *
 * The latch is deliberately one-way: there is no `undismiss()`. Re-showing the banner within a
 * session is the drag gesture's job (`attractionsBannerCommitted` on MenuComponent), not this
 * service's.
 */
@Injectable({ providedIn: 'root' })
export class AttractionsEntryService {
  private dismissed = new BehaviorSubject<boolean>(false);

  /**
   * Emits on dismissal. Currently unsubscribed — the visibility getters on `MenuComponent` read the
   * synchronous `isDismissed` instead, since they are evaluated during change detection. Kept for
   * symmetry with the house service shape (`AttractionDetailsSheetService.changes$`) and so a future
   * consumer outside a template does not have to poll.
   */
  readonly dismissed$: Observable<boolean> = this.dismissed.asObservable();

  get isDismissed(): boolean {
    return this.dismissed.value;
  }

  dismissBanner(): void {
    this.dismissed.next(true);
  }
}
